import PocketBase from 'pocketbase';
import { PB_BASE_URL, isPlatformAdminRole, isTenantAdminRole } from '../config/constants.js';

// Create PocketBase client instance
const pb = new PocketBase(PB_BASE_URL);

// --- Auth State Persistence Helpers ---
// Save auth data to localStorage with timestamp
export const saveAuthToken = () => {
  if (pb.authStore.isValid) {
    const authData = {
      token: pb.authStore.token,
      model: pb.authStore.model,
      lastActivity: Date.now()
    };
    localStorage.setItem('bpas_auth', JSON.stringify(authData));
  }
};

// Load auth data from localStorage and restore to PocketBase store
export const loadAuthToken = () => {
  try {
    const raw = localStorage.getItem('bpas_auth');
    if (raw) {
      const data = JSON.parse(raw);
      if (data.token && data.model) {
        pb.authStore.save(data.token, data.model);
        return data; // Return full object including lastActivity
      }
    }
  } catch (e) {
    console.error("Failed to parse auth token", e);
  }
  return null;
};

// Clear auth data from both PocketBase and localStorage
export const logout = () => {
  localStorage.removeItem('bpas_auth');
  pb.authStore.clear();
};

// Clear auth data from both PocketBase and localStorage (redundant but kept for compatibility)
export const clearAuthToken = () => {
  localStorage.removeItem('bpas_auth');
  pb.authStore.clear();
};

// Update just the activity timestamp in localStorage (for idle timeout)
export const updateAuthActivity = () => {
  if (pb.authStore.isValid) {
    const raw = localStorage.getItem('bpas_auth');
    if (raw) {
      try {
        const data = JSON.parse(raw);
        data.lastActivity = Date.now();
        localStorage.setItem('bpas_auth', JSON.stringify(data));
      } catch (e) {
        // Ignore JSON errors during activity update
      }
    }
  }
};

// --- Auth Functions ---
export const login = async (email, password) => {
  try {
    // Expand tenant to check status immediately upon login
    const authData = await pb.collection('portal_users').authWithPassword(email, password, {
      expand: 'tenant'
    });

    // Check if tenant is archived
    const tenant = authData.record.expand?.tenant;
    if (tenant && tenant.is_archived) {
      pb.authStore.clear(); // Clear the token immediately
      throw new Error('Your account is no longer active. Please contact BPA Solutions.');
    }

    // saveAuthToken will be called by the context after successful login processing
    return authData;
  } catch (error) {
    // Preserve our custom error message
    if (error.message.includes('Your account is no longer active')) {
      throw error;
    }
    throw new Error(error.message || 'Login failed');
  }
};

// Refresh the current auth token and validate session
export const refreshAuth = async () => {
  try {
    // requesting expand: 'tenant' to ensure we have full user context
    const authData = await pb.collection('portal_users').authRefresh({
      expand: 'tenant'
    });

    // Check if tenant is archived
    const tenant = authData.record.expand?.tenant;
    if (tenant && tenant.is_archived) {
      throw new Error('Your account is no longer active. Please contact BPA Solutions.');
    }

    saveAuthToken(); // Update storage with fresh token and timestamp
    return authData.record;
  } catch (error) {
    clearAuthToken(); // Force cleanup if token is invalid or archived
    throw error;
  }
};

export const getCurrentUser = () => {
  return pb.authStore.model;
};

export const getAuthToken = () => {
  return pb.authStore.token;
};

// Password Reset Request
export const requestPasswordReset = async (email) => {
  try {
    return await pb.collection('portal_users').requestPasswordReset(email);
  } catch (error) {
    throw new Error(error.message || 'Failed to request password reset');
  }
};

// User functions
export const fetchUserById = async (userId) => {
  try {
    return await pb.collection('portal_users').getOne(userId, {
      expand: 'tenant'
    });
  } catch (error) {
    throw new Error(error.message || 'Failed to fetch user');
  }
};

export const updateUserPassword = async (userId, oldPassword, newPassword) => {
  try {
    const data = {
      password: newPassword,
      passwordConfirm: newPassword,
      oldPassword: oldPassword
    };
    return await pb.collection('portal_users').update(userId, data);
  } catch (error) {
    throw new Error(error.message || 'Failed to update password');
  }
};

export const resetUserPassword = async (userId, newPassword) => {
  try {
    const data = {
      password: newPassword,
      passwordConfirm: newPassword
    };
    return await pb.collection('portal_users').update(userId, data);
  } catch (error) {
    throw new Error(error.message || 'Failed to reset password');
  }
};

// Tenant functions
export const fetchTenants = async (filters = {}) => {
  try {
    const user = pb.authStore.model;
    const role = user?.role;
    const conditions = [];

    // Handle generic filters (excluding 'status' which is special logic now)
    Object.entries(filters).forEach(([key, value]) => {
      if (key !== 'status') {
        conditions.push(`${key}~"${value}"`);
      }
    });

    // Robust role checking
    const isSuperOrBpasAdmin = isPlatformAdminRole(role);
    const isClientAdmin = isTenantAdminRole(role);

    if (isSuperOrBpasAdmin) {
      // Respect Active/Archived tabs via 'status' filter
      if (filters.status === 'active') {
        conditions.push('is_archived=false');
      } else if (filters.status === 'archived') {
        conditions.push('is_archived=true');
      }
    } else if (isClientAdmin) {
      const tenantId = user?.tenant;
      if (tenantId) {
        conditions.push(`id="${tenantId}"`);
      } else {
        return [];
      }
    } else {
      return [];
    }

    const filterStr = conditions.join(' && ');
    return await pb.collection('tenants').getFullList({
      filter: filterStr,
      sort: '-created'
    });
  } catch (error) {
    throw new Error(error.message || 'Failed to fetch tenants');
  }
};

export const createTenant = async (data) => {
  try {
    return await pb.collection('tenants').create(data);
  } catch (error) {
    throw new Error(error.message || 'Failed to create tenant');
  }
};

export const updateTenant = async (id, data) => {
  try {
    return await pb.collection('tenants').update(id, data);
  } catch (error) {
    throw new Error(error.message || 'Failed to update tenant');
  }
};

export const deleteTenant = async (id) => {
  try {
    return await pb.collection('tenants').delete(id);
  } catch (error) {
    throw new Error(error.message || 'Failed to delete tenant');
  }
};

// Portal Users functions
export const fetchPortalUsers = async (filters = {}) => {
  try {
    const filterStr = Object.keys(filters).length > 0
      ? Object.entries(filters).map(([key, value]) => `${key}~"${value}"`).join(' && ')
      : '';
    return await pb.collection('portal_users').getFullList({
      filter: filterStr,
      expand: 'tenant',
      sort: '-created'
    });
  } catch (error) {
    throw new Error(error.message || 'Failed to fetch portal users');
  }
};

export const createPortalUser = async (data) => {
  try {
    return await pb.collection('portal_users').create(data);
  } catch (error) {
    throw new Error(error.message || 'Failed to create portal user');
  }
};

export const updatePortalUser = async (id, data) => {
  try {
    return await pb.collection('portal_users').update(id, data);
  } catch (error) {
    throw new Error(error.message || 'Failed to update portal user');
  }
};

// Enquiries functions
let allowedSourceChannelsCache = null;

const FALLBACK_ENQUIRY_SOURCE_CHANNELS = [
  'google',
  'facebook',
  'instagram',
  'word_of_mouth',
  'other',
  'Airtasker',
  'Client Hub (Manual)',
  'LinkedIn',
  'Website',
  'Referral'
];

const ensureAllowedSourceChannels = async () => {
  if (allowedSourceChannelsCache) return allowedSourceChannelsCache;
  try {
    // PocketBase collection metadata is normally admin-only. If available, support both
    // current fields[].values and older schema[].options.values response shapes.
    const collection = await pb.send("/api/collections/enquiries", { method: "GET" });
    const currentField = collection?.fields?.find(f => f.name === 'source_channel');
    const legacyField = collection?.schema?.find(f => f.name === 'source_channel');
    const values = currentField?.values || currentField?.options?.values || legacyField?.options?.values;
    if (Array.isArray(values) && values.length > 0) {
      allowedSourceChannelsCache = values;
    }
  } catch (e) {
    console.warn("Failed to fetch source_channel schema; using known safe fallback values:", e);
  }
  return allowedSourceChannelsCache;
};

const mapToAllowedSelect = (value, allowedValues) => {
  if (!allowedValues || allowedValues.length === 0) return null;
  if (!value) return null;
  const normalized = String(value).trim().toLowerCase();
  return allowedValues.find(v => v.toLowerCase() === normalized) || null;
};

export const fetchEnquiriesForTenant = async (tenantId, filters = {}) => {
  try {
    let filterStr = `tenant="${tenantId}"`;
    if (filters.status && filters.status.length > 0) {
      const statusFilter = filters.status.map(s => `status="${s}"`).join(' || ');
      filterStr += ` && (${statusFilter})`;
    }
    if (filters.enquiry_type && filters.enquiry_type.length > 0) {
      const typeFilter = filters.enquiry_type.map(t => `enquiry_type="${t}"`).join(' || ');
      filterStr += ` && (${typeFilter})`;
    }
    if (filters.source_channel && filters.source_channel.length > 0) {
      const channelFilter = filters.source_channel.map(c => `source_channel="${c}"`).join(' || ');
      filterStr += ` && (${channelFilter})`;
    }
    if (filters.dateFrom) {
      filterStr += ` && created>="${filters.dateFrom}"`;
    }
    if (filters.dateTo) {
      filterStr += ` && created<="${filters.dateTo}"`;
    }
    if (filters.service) {
      filterStr += ` && service_requested~"${filters.service}"`;
    }
    return await pb.collection('enquiries').getFullList({
      filter: filterStr,
      sort: '-created'
    });
  } catch (error) {
    throw new Error(error.message || 'Failed to fetch enquiries');
  }
};

export const createEnquiryForTenant = async (tenantId, data) => {
  try {
    // 1. Resolve source_channel validation
    const allowed = await ensureAllowedSourceChannels();
    let validSource = data.source_channel || 'Client Hub (Manual)';

    // Normalize legacy/internal values to PocketBase allowed select values
    if (String(validSource).toLowerCase() === "client_hub") {
      validSource = "Client Hub (Manual)";
    }
    if (String(validSource).toLowerCase() === "other") {
      validSource = "other";
    }

    if (allowed && allowed.length > 0) {
      const mapped = mapToAllowedSelect(validSource, allowed);
      const fallbackOther = mapToAllowedSelect('other', allowed);
      validSource = mapped || fallbackOther || allowed[0];
    } else {
      const normalizedLower = String(validSource || "").trim().toLowerCase();
      const matched = FALLBACK_ENQUIRY_SOURCE_CHANNELS.find(v => v.toLowerCase() === normalizedLower);
      validSource = matched || 'other';
    }

    // 2. Create a safe copy
    const safe = { ...data };

    // 3. Remove forbidden fields
    delete safe.id;
    delete safe.tenant;
    delete safe.created;
    delete safe.updated;
    delete safe.collectionId;
    delete safe.collectionName;

    // 4. Construct explicit payload
    const payload = {
      tenant: tenantId,
      full_name: safe.full_name,
      email: safe.email,
      phone: safe.phone,
      enquiry_type: safe.enquiry_type,
      service_requested: safe.service_requested,
      message: safe.message,
      internal_note: safe.internal_note,
      follow_up_date: safe.follow_up_date || '',
      source_channel: validSource,
      status: safe.status || 'new',
      priority: safe.priority || 'normal'
    };

    // 5. Send to PocketBase
    return await pb.collection('enquiries').create(payload);
  } catch (error) {
    console.error('PB create error:', error?.data || error?.message);
    const details = error?.data ? JSON.stringify(error.data) : "";
    throw new Error((error?.message || "Failed to create record.") + (details ? " | " + details : ""));
  }
};

export const updateEnquiry = async (id, data) => {
  try {
    return await pb.collection('enquiries').update(id, data);
  } catch (error) {
    throw new Error(error.message || 'Failed to update enquiry');
  }
};

export const deleteEnquiry = async (id) => {
  try {
    return await pb.collection('enquiries').delete(id);
  } catch (error) {
    throw new Error(error.message || 'Failed to delete enquiry');
  }
};

// Blog functions
export const fetchBlogPostsForTenant = async (tenantId, filters = {}) => {
  try {
    let filterStr = `tenant="${tenantId}"`;
    if (filters.status) {
      filterStr += ` && status="${filters.status}"`;
    }
    return await pb.collection('blog_posts').getFullList({
      filter: filterStr,
      sort: '-created'
    });
  } catch (error) {
    throw new Error(error.message || 'Failed to fetch blog posts');
  }
};

export const createBlogPost = async (data) => {
  try {
    return await pb.collection('blog_posts').create(data);
  } catch (error) {
    throw new Error(error.message || 'Failed to create blog post');
  }
};

export const updateBlogPost = async (id, data) => {
  try {
    return await pb.collection('blog_posts').update(id, data);
  } catch (error) {
    throw new Error(error.message || 'Failed to update blog post');
  }
};

export const deleteBlogPost = async (id) => {
  try {
    return await pb.collection('blog_posts').delete(id);
  } catch (error) {
    throw new Error(error.message || 'Failed to delete blog post');
  }
};

// Business Settings functions
export const fetchBusinessSettingsForTenant = async (tenantId) => {
  try {
    const records = await pb.collection('business_settings').getFullList({
      filter: `tenant="${tenantId}"`
    });
    return records.length > 0 ? records[0] : null;
  } catch (error) {
    throw new Error(error.message || 'Failed to fetch business settings');
  }
};

export const upsertBusinessSettingsForTenant = async (tenantId, data) => {
  try {
    const existing = await fetchBusinessSettingsForTenant(tenantId);
    if (existing) {
      return await pb.collection('business_settings').update(existing.id, data);
    } else {
      return await pb.collection('business_settings').create({ ...data, tenant: tenantId });
    }
  } catch (error) {
    throw new Error(error.message || 'Failed to save business settings');
  }
};

// Change Requests functions
export const fetchChangeRequestsForTenant = async (tenantId, filters = {}) => {
  try {
    let filterStr = `tenant="${tenantId}"`;
    if (filters.status && filters.status.length > 0) {
      const statusFilter = filters.status.map(s => `status="${s}"`).join(' || ');
      filterStr += ` && (${statusFilter})`;
    }
    if (filters.priority && filters.priority.length > 0) {
      const priorityFilter = filters.priority.map(p => `priority="${p}"`).join(' || ');
      filterStr += ` && (${priorityFilter})`;
    }
    return await pb.collection('change_requests').getFullList({
      filter: filterStr,
      expand: 'requested_by,tenant',
      sort: '-created'
    });
  } catch (error) {
    throw new Error(error.message || 'Failed to fetch change requests');
  }
};

export const createChangeRequest = async (data) => {
  try {
    return await pb.collection('change_requests').create(data);
  } catch (error) {
    throw new Error(error.message || 'Failed to create change request');
  }
};

export const updateChangeRequest = async (id, data) => {
  try {
    return await pb.collection('change_requests').update(id, data);
  } catch (error) {
    throw new Error(error.message || 'Failed to update change request');
  }
};

export const fetchAllChangeRequests = async (filters = {}) => {
  try {
    let filterStr = '';
    const filterParts = [];
    if (filters.status && filters.status.length > 0) {
      const statusFilter = filters.status.map(s => `status="${s}"`).join(' || ');
      filterParts.push(`(${statusFilter})`);
    }
    if (filters.tenant) {
      filterParts.push(`tenant="${filters.tenant}"`);
    }
    if (filters.priority && filters.priority.length > 0) {
      const priorityFilter = filters.priority.map(p => `priority="${p}"`).join(' || ');
      filterParts.push(`(${priorityFilter})`);
    }
    if (filterParts.length > 0) {
      filterStr = filterParts.join(' && ');
    }
    return await pb.collection('change_requests').getFullList({
      filter: filterStr,
      expand: 'requested_by,tenant',
      sort: '-created'
    });
  } catch (error) {
    throw new Error(error.message || 'Failed to fetch change requests');
  }
};

export const fetchHelpVideos = async (includeInactive = false, audiences = []) => {
  try {
    const clauses = [];
    if (!includeInactive) {
      clauses.push('is_active=true');
    }
    if (audiences && audiences.length > 0) {
      const audienceFilter = audiences.map(a => `audience="${a}"`).join(' || ');
      clauses.push(`(${audienceFilter})`);
    }
    const filterStr = clauses.join(' && ');
    return await pb.collection('help_videos').getFullList({
      filter: filterStr,
      sort: 'sort_order,title'
    });
  } catch (error) {
    if (error.status === 404) return [];
    throw error;
  }
};

export const createHelpVideo = async (data) => {
  try {
    const { tenant, order, id, ...rest } = data;
    const payload = {
      ...rest,
      sort_order: Number.isFinite(Number(data.sort_order)) ? Number(data.sort_order) : (Number(data.order) || 0),
      category: data.category || 'Getting Started',
      audience: data.audience || 'All'
    };
    return await pb.collection('help_videos').create(payload);
  } catch (error) {
    throw error;
  }
};

export const updateHelpVideo = async (id, data) => {
  try {
    const { tenant, order, ...rest } = data;
    const payload = { ...rest };
    if (data.sort_order !== undefined) {
      payload.sort_order = Number(data.sort_order);
    } else if (data.order !== undefined) {
      payload.sort_order = Number(data.order);
    }
    return await pb.collection('help_videos').update(id, payload);
  } catch (error) {
    throw error;
  }
};

export const deleteHelpVideo = async (id) => {
  try {
    return await pb.collection('help_videos').delete(id);
  } catch (error) {
    throw error;
  }
};

export default pb;